import { create } from 'zustand';
import JsBarcode from 'jsbarcode';
import { Capacitor } from '@capacitor/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { StarPRNT, Emulation, PrinterStatus } from '@awesome-cordova-plugins/star-prnt';
import {
  ProductTypeEnum,
  BusinessDataType,
  CheckoutDataType,
  CheckoutItemType,
  ActionStatusEnum,
  DailySummaryType,
  DailySummaryByPeriod,
  DailySummaryPeriodRangeType,
  CompletedPaymentTransactionType,
} from '@am3nd-org/pointcraft-types';

import { withLock } from 'src/utils/printer-lock';
import { fCurrency } from 'src/utils/format-number';

import { printSaleReceiptBitmap } from './v4/sale-receipt';
import { PrinterStoreType, PrinterLastError } from './types';

function captureError(error: unknown, context: string): PrinterLastError {
  const message = error instanceof Error ? error.message : String(error);
  return { message, timestamp: Date.now(), context };
}

export const usePrinterStore = create<PrinterStoreType>((set, get) => ({
  isPrinterReady: false,
  currentlyPrinting: false,
  status: null,
  lastError: null,
  troubleshootOpen: false,
  pendingRetry: null,

  openTroubleshoot: (retryFn) => {
    set({ troubleshootOpen: true, pendingRetry: retryFn ?? null });
  },
  closeTroubleshoot: () => {
    set({ troubleshootOpen: false, pendingRetry: null });
  },

  setIsPrinterReady: (isReady: boolean) => {
    console.log('[PrinterStore] Setting printer ready:', isReady);
    set((state) => ({
      ...state,
      isPrinterReady: isReady,
    }));
  },
  setPrinterIsReadyWithStatus: (status: PrinterStatus & { overTemp?: boolean }) => {
    console.log('[PrinterStore] Setting printer ready with status:', status);
    set((state) => ({
      ...state,
      isPrinterReady: true,
      status: {
        ...state.status,
        coverOpen: status.coverOpen,
        cutterError: status.cutterError,
        offline: status.offline,
        overTemp: status?.overTemp ?? false,
        receiptPaperEmpty: status.receiptPaperEmpty,
      },
    }));
  },
  showAllPrinters: () => {
    console.log('Showing all printers');
    return [1];
  },
  printReceipt: (receipt: string) => {
    console.log('Printing receipt:', receipt);
    if (get().isPrinterReady) {
      console.info('Printer is ready and printing now');
      const printObj = {
        text: '\ns\ns\ns\ns\ns\ns\ns\ns\ns\nsStar Clothing Boutique\n123 Star Road\nCity, State 12345\n\n',
        cutReceipt: true,
        openCashDrawer: false,
      };
      StarPRNT.printRasterReceipt('BT:TSP100', 'StarGraphic', printObj).catch((error: unknown) => {
        console.error('[PrinterStore] printRasterReceipt failed:', error);
      });
      return true;
    }

    console.error('Printer is not ready, cannot print now');
    return false;
  },
  printTestReceipt: (receipt: string) => {
    console.log('Printing test receipt:', receipt);
    if (get().isPrinterReady) {
      console.info('Printer is ready and printing now');
      const printObj = {
        text: receipt,
        cutReceipt: true,
      };
      StarPRNT.printRasterReceipt('BT:TSP100', 'StarGraphic', printObj).catch((error: unknown) => {
        console.error('[PrinterStore] printTestReceipt failed:', error);
      });
      return true;
    }

    console.error('Printer is not ready, cannot print now');
    return false;
  },
  openCashDrawer: () => {
    console.log('Opening cash drawer...');
    if (get().isPrinterReady) {
      console.info('Printer is ready, opening cash drawer now');
      withLock(async () => {
        await StarPRNT.openCashDrawer('BT:TSP100', 'StarGraphic');
      }).catch((error) => {
        console.error('Error opening cash drawer:', error);
      });
      return true;
    }

    console.error('Printer is not ready, cannot open cash drawer');
    return false;
  },
  reconnectPrinter: async () => {
    console.log('[PrinterStore] Manual printer reconnection triggered');
    await withLock(async () => {
      try {
        const port = 'BT:TSP100';
        const emulation = Emulation.StarGraphic;

        const printers = await StarPRNT.portDiscovery('All');
        console.log('[PrinterStore] Found printers during manual reconnect:', printers);

        if (printers.length > 0) {
          const btPrinters = printers.filter((p: any) => p.portName?.startsWith('BT:'));
          const tsp100Printer =
            btPrinters.find((p: any) => p.portName === port) ||
            btPrinters[0] ||
            printers.find((p: any) => p.portName === port) ||
            printers[0];
          const printerPort = tsp100Printer?.portName || port;

          if (printerPort.startsWith('TCP:')) {
            console.log('[PrinterStore] Skipping status check for TCP printer (SDK bug workaround)');
            set((state) => ({ ...state, isPrinterReady: false }));
            return;
          }

          const status = await StarPRNT.checkStatus(printerPort, emulation);
          console.log('[PrinterStore] Manual reconnect status:', status);

          if (!status.offline && !status.coverOpen && !status.cutterError && !status.receiptPaperEmpty) {
            console.log('[PrinterStore] Printer reconnected successfully');
            set((state) => ({
              ...state,
              isPrinterReady: true,
              status: {
                coverOpen: status.coverOpen,
                cutterError: status.cutterError,
                offline: status.offline,
                overTemp: false,
                receiptPaperEmpty: status.receiptPaperEmpty,
              },
            }));
          } else {
            console.log('[PrinterStore] Printer has issues during reconnect');
            set((state) => ({ ...state, isPrinterReady: false }));
          }
        } else {
          console.log('[PrinterStore] No printers found during manual reconnect');
          set((state) => ({ ...state, isPrinterReady: false }));
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('permission') || errorMessage.includes('SecurityException')) {
          console.warn('[PrinterStore] Bluetooth permission denied during reconnection');
        } else {
          console.error('[PrinterStore] Error during manual printer reconnection:', error);
        }
        set((state) => ({ ...state, isPrinterReady: false, lastError: captureError(error, 'reconnect') }));
      }
    });
  },

  resetConnection: async (onStep) => {
    console.log('[PrinterStore] Full connection reset triggered');
    await withLock(async () => {
      try {
        // Step 1: Disconnect existing printer connection
        onStep?.('Disconnecting printer...');
        try {
          await StarPRNT.disconnect();
          console.log('[PrinterStore] Disconnected existing connection');
        } catch {
          // disconnect may fail if no active connection — safe to ignore
        }

        // Step 2: Cycle Bluetooth off → on (native only)
        if (Capacitor.isNativePlatform()) {
          onStep?.('Turning off Bluetooth...');
          try {
            await BleClient.initialize();
            await BleClient.disable();
            console.log('[PrinterStore] Bluetooth disabled');

            // Brief pause to let the radio fully power down
            await new Promise((resolve) => { setTimeout(resolve, 1500); });

            onStep?.('Turning on Bluetooth...');
            await BleClient.enable();
            console.log('[PrinterStore] Bluetooth re-enabled');

            // Wait for BT stack to stabilise before discovery
            await new Promise((resolve) => { setTimeout(resolve, 2000); });
          } catch (btErr) {
            console.warn('[PrinterStore] Bluetooth cycle skipped:', btErr);
            // Non-fatal — continue with discovery even if BT toggle failed
          }
        }

        // Step 3: Discover printers
        onStep?.('Searching for printers...');
        const port = 'BT:TSP100';
        const emulation = Emulation.StarGraphic;

        const printers = await StarPRNT.portDiscovery('All');
        console.log('[PrinterStore] Discovery after reset:', printers);

        if (printers.length === 0) {
          set((state) => ({
            ...state,
            isPrinterReady: false,
            lastError: captureError('No printers found after reset', 'reset-connection'),
          }));
          return;
        }

        const btPrinters = printers.filter((p: any) => p.portName?.startsWith('BT:'));
        const tsp100Printer =
          btPrinters.find((p: any) => p.portName === port) ||
          btPrinters[0] ||
          printers.find((p: any) => p.portName === port) ||
          printers[0];
        const printerPort = tsp100Printer?.portName || port;

        if (printerPort.startsWith('TCP:')) {
          console.log('[PrinterStore] Skipping TCP printer during reset');
          set((state) => ({ ...state, isPrinterReady: false }));
          return;
        }

        // Step 4: Verify printer status
        onStep?.('Verifying printer status...');
        const status = await StarPRNT.checkStatus(printerPort, emulation);
        console.log('[PrinterStore] Status after reset:', status);

        if (!status.offline && !status.coverOpen && !status.cutterError && !status.receiptPaperEmpty) {
          console.log('[PrinterStore] Printer reconnected after reset');
          set((state) => ({
            ...state,
            isPrinterReady: true,
            lastError: null,
            status: {
              coverOpen: status.coverOpen,
              cutterError: status.cutterError,
              offline: status.offline,
              overTemp: false,
              receiptPaperEmpty: status.receiptPaperEmpty,
            },
          }));
        } else {
          set((state) => ({
            ...state,
            isPrinterReady: false,
            status: {
              coverOpen: status.coverOpen,
              cutterError: status.cutterError,
              offline: status.offline,
              overTemp: false,
              receiptPaperEmpty: status.receiptPaperEmpty,
            },
          }));
        }
      } catch (error) {
        console.error('[PrinterStore] Reset connection failed:', error);
        set((state) => ({
          ...state,
          isPrinterReady: false,
          lastError: captureError(error, 'reset-connection'),
        }));
        throw error;
      }
    });
  },
}));

/**
 *

  const { example, exampleVar: false } = useExampleDataStore((state) => ({
    currentInventory: state.currentInventory,
    newInventory: state.newInventory,
  }));

  const setExample = useExampleDataStore.getState().setExample();

 */

export const printReceipt = async (receipt: ReceiptStringType): Promise<boolean> => {
  const { isPrinterReady } = usePrinterStore.getState();
  if (!isPrinterReady) {
    console.error('[PrinterStore] Printer is not ready, cannot print now');
    return false;
  }

  console.info('Printer is ready and printing now');
  usePrinterStore.setState({ isPrinterReady: false, currentlyPrinting: true });
  try {
    await withLock(async () => {
      await StarPRNT.printRasterReceipt('BT:TSP100', 'StarGraphic', {
        text: receipt,
        cutReceipt: true,
      });
    });
    setTimeout(() => {
      usePrinterStore.setState({ isPrinterReady: true, currentlyPrinting: false });
    }, 4000);
    return true;
  } catch (error) {
    console.error('[PrinterStore] printRasterReceipt failed:', error);
    usePrinterStore.setState({
      isPrinterReady: false,
      currentlyPrinting: false,
      lastError: captureError(error, 'text-receipt'),
    });
    throw error;
  }
};

/**
 * Print the Square-style bitmap sales receipt to the local thermal printer.
 *
 * Drop-in replacement for `printReceipt` (same printer-ready guard, state
 * toggling and boolean return), but renders the modern bitmap receipt via the
 * V4 `printImage` path instead of the legacy 38-char text receipt.
 */
export const printSaleReceipt = async (
  checkout: CheckoutDataType,
  business: BusinessDataType
): Promise<boolean> => {
  const { isPrinterReady } = usePrinterStore.getState();
  if (!isPrinterReady) {
    console.error('[PrinterStore] Printer is not ready, cannot print now');
    return false;
  }

  usePrinterStore.setState({ isPrinterReady: false, currentlyPrinting: true });
  try {
    await printSaleReceiptBitmap(checkout, business);
    setTimeout(() => {
      usePrinterStore.setState({ isPrinterReady: true, currentlyPrinting: false });
    }, 4000);
    return true;
  } catch (error) {
    console.error('[PrinterStore] printSaleReceipt failed:', error);
    usePrinterStore.setState({
      isPrinterReady: false,
      currentlyPrinting: false,
      lastError: captureError(error, 'sale-receipt'),
    });
    throw error;
  }
};

interface ReceiptItem {
  sku: string;
  description: string;
  price: number;
}

interface ReceiptData {
  businessName: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  date: string;
  time: string;
  items: ReceiptItem[];
  subtotal: number;
  tax: number;
  total: number;
  cardType: string;
  cardNumber: string;
}
export type ReceiptStringType = string;
/**
 *
 *  Date,
 * Business Name
 *  Business Address
 * products
 * subtotal
 * processing
 * taxes
 * total
 * payments
 *
 *
 */
export function generateReceiptStringV3(
  // currentCheckout: CheckoutDataType,
  {
    items,
    date,
    address,
    businessName,
    financialInfo,
    customer,
  }: {
    items: CheckoutItemType[];
    date: string;
    address: {
      street1: string;
      city: string;
      state: string;
      postalCode: string;
    };
    businessName: string;
    financialInfo: {
      subTotal: number;
      taxes: number;
      processingFees: number;
      total: number;
      payments: CompletedPaymentTransactionType[];
    };
    customer?: {
      name: string;
      phone: string;
    };
  }
): ReceiptStringType {
  // Maximum of 38 characters per line
  let receipt = `${businessName}\n${address?.street1 ?? '...'}\n${address?.city ?? '...'}, ${
    address?.state ?? '...'
  } ${address?.postalCode ?? '...'}\n\nDate:${new Date(
    date
  ).toLocaleDateString()}    Time:${new Date(
    date
  ).toLocaleTimeString()}\n--------------------------------------\nSALE\n`;

  // We need to ensure we leave at least 9 characters for the price $1,234.56 plus space.
  // Sku can have 8 characters plus space and description can have 20 characters plus space
  // We need to push to the next line if overflows. and ensure it is aligned
  receipt += `Item         QTY         Total\n`;
  console.log('items', items);
  const itemsString = items
    .map((item) => {
      const skuSubLengthStart = item?.id?.length > 8 ? item.id.length - 8 : 0;
      const sku = item?.id.substring(skuSubLengthStart).padEnd(15);
      const qty = item.quantity.toString();
      const { name } = item;
      // item.name.length ;// <= 20 ? item.name.padEnd(20) : item.name.substring(0, 20);

      const identifier =
        item?.linkedProduct?.type === ProductTypeEnum.ELECTRONIC_DEVICE
          ? `IMEI: ${item?.linkedProduct?.identifier}`
          : `ID: ${item?.identifier}`;

      const total = fCurrency(item.price * item.quantity).padStart(9);
      const returnString = `${sku}${qty.padEnd(12)}${total}\n${name}\n${identifier}\n`; // TODO: add dollar sign and update whole function fo receipt printing.
      return returnString;
      // console.log('sku', sku, 'description', description, 'price', price);
      // if (remainingDescription) {
      //   return `${sku}${description}${price}\n            ${remainingDescription}\n`;
      // }
      // return `${sku}${description}${price}\n`;
    })
    .join('');
  receipt += itemsString;

  receipt += `\nSubtotal${fCurrency(financialInfo.subTotal).padStart(25)}`;

  const fees = financialInfo.taxes + financialInfo.processingFees;
  receipt += `\nTaxes & Fees${fCurrency(fees).padStart(21)}
    \n--------------------------------------\n`;

  receipt += `Total${`$${financialInfo.total.toFixed(2)}`.padStart(
    28
  )}\n--------------------------------------\n`;

  const paymentsItemString = financialInfo.payments.map((payment) => {
    // if payment failed
    // if (payment.status !== PaymentStatusEnum.SUCCESS && payment.status !== "SUCCEEDED") {
    if (payment.status !== ActionStatusEnum.SUCCESS) {
      return ``;
    }
    const paymentType =
      payment.cardType === 'CREDIT' || payment.cardType === 'MANUAL_CARD' ? 'CREDIT CARD' : 'CASH';
    return `${paymentType.padEnd(18)}${fCurrency(payment.amount).padEnd(20)}\n`;
  });
  receipt += `\nPayments\n${paymentsItemString.join('')}\n`;
  const customerNameAndPhoneString = customer
    ? `\nCustomer: ${customer.name}\nPhone: ${customer.phone}\n`
    : '';
  receipt += `${customerNameAndPhoneString}`;
  receipt += `\nRefunds and Exchanges\nWithin 30 days with receipt\nAnd tags attached`;
  return `\n\n\n${appendPointCraftFooter(receipt)} \n\n\n`;
}

export function generateReceiptString2(
  currentCheckout: CheckoutDataType,
  businessData: BusinessDataType,
  locationId: string
): ReceiptStringType {
  // Maximum of 38 characters per line
  const address = businessData.locations.find((loc) => loc.id === locationId)?.address;
  let receipt = `${businessData.name}\n${address?.street1 ?? '...'}\n${address?.city ?? '...'}, ${
    address?.state ?? '...'
  } ${address?.postalCode ?? '...'}\n\nDate:${new Date(
    currentCheckout.details.date
  ).toLocaleDateString()}    Time:${new Date(
    currentCheckout.details.date
  ).toLocaleTimeString()}\n--------------------------------------\nSALE\n`;

  // We need to ensure we leave at least 9 characters for the price $1,234.56 plus space.
  // Sku can have 8 characters plus space and description can have 20 characters plus space
  // We need to push to the next line if overflows. and ensure it is aligned
  receipt += `Item         QTY         Total\n`;
  const itemsString = currentCheckout.items
    .map((item) => {
      const skuSubLengthStart = item.identifier.length > 8 ? item.identifier.length - 8 : 0;
      const sku = item.identifier.substring(skuSubLengthStart).padEnd(15);
      const qty = item.quantity.toString();
      const name = item.name.length <= 20 ? item.name.padEnd(20) : item.name.substring(0, 20);

      const total = fCurrency(item.price * item.quantity).padStart(9);
      const returnString = `${sku}${qty.padEnd(12)}${total}\n${name}\n\n`; // TODO: add dollar sign and update whole function fo receipt printing.
      return returnString;
      // console.log('sku', sku, 'description', description, 'price', price);
      // if (remainingDescription) {
      //   return `${sku}${description}${price}\n            ${remainingDescription}\n`;
      // }
      // return `${sku}${description}${price}\n`;
    })
    .join('');
  receipt += itemsString;

  receipt += `\nSubtotal${fCurrency(currentCheckout.financialInfo.subTotal).padStart(25)}`;

  const fees = currentCheckout.financialInfo.taxes + currentCheckout.financialInfo.processingFees;
  receipt += `\nTaxes & Fees${fCurrency(fees).padStart(21)}
    \n--------------------------------------\n`;

  receipt += `Total${`$${currentCheckout.financialInfo.total.toFixed(2)}`.padStart(
    28
  )}\n--------------------------------------\n`;

  const paymentsItemString = currentCheckout.financialInfo.payment.transactions.map((payment) => {
    // if payment failed
    if (payment.status !== ActionStatusEnum.SUCCESS) {
      return ``;
    }
    const paymentType =
      payment.type === 'CARD' || payment.type === 'MANUAL_CARD' ? 'CREDIT CARD' : 'CASH';
    return `${paymentType.padEnd(18)}${fCurrency(payment.amount).padEnd(20)}\n`;
  });
  receipt += `\nPayments\n${paymentsItemString.join('')}\n`;
  receipt += `\nRefunds and Exchanges\nWithin 30 days with receipt\nAnd tags attached`;
  return receipt;
}

export function generateReceiptString(data: ReceiptData): ReceiptStringType {
  const {
    businessName,
    streetAddress,
    city,
    state,
    zip,
    date,
    time,
    items,
    subtotal,
    tax,
    total,
    cardType,
    cardNumber,
  } = data;

  const lastFour = cardNumber.slice(-4);
  const maskedCardNumber = `XXXX-XXXX-XXXX-${lastFour}`;

  let receipt = `${businessName}\n${streetAddress}\n${city}, ${state} ${zip}\n\nDate:${date}    Time:${time}\n------------------------------------------\nSALE\nSKU         Description         Total\n`;

  // for (const item of items) {
  //   receipt += `${item.sku.padEnd(12)}${item.description.padEnd(20)}${item.price.toFixed(2)}\n`;
  // }
  const itemsString = items
    .map((item) => `${item.sku.padEnd(12)}${item.description.padEnd(20)}${item.price.toFixed(2)}\n`)
    .join('');
  receipt += itemsString;

  receipt += `\nSubtotal${subtotal.toFixed(2).padStart(25)}\nTax${tax
    .toFixed(2)
    .padStart(30)}\n------------------------------------------\nTotal${total
    .toFixed(2)
    .padStart(
      28
    )}\n------------------------------------------\n\nCharge\n${cardType} ${maskedCardNumber}\nRefunds and Exchanges\nWithin 30 days with receipt\nAnd tags attached`;

  return receipt;
}

/** ──────────────────────────────────────────────────────────────
 * Helper to pad the amount so that every line is ≤ 38 chars.    */
const makeLine = (label: string, amount: string): string =>
  `${label}${amount.padStart(38 - label.length)}`;

/** Convert “12345” cents → “$123.45” using the same currency
 *  formatter the rest of the POS already relies on.             */
const cents = (c: number): string => fCurrency(c / 100);

/** Main generator – returns a printable 38-column receipt. */
export function generateDailySummaryReceiptString(params: {
  summary: DailySummaryType | DailySummaryByPeriod;
  business: BusinessDataType;
  /** Optional when summary has “date”; required for
   *  DailySummaryByPeriod so the cashier sees the range.       */
  period?: DailySummaryPeriodRangeType;
}): string {
  const { summary, business, period } = params;
  const {
    gross_sales_cents,
    cash_sales_cents,
    card_sales_cents,
    debt_amount_cents,
    taxes_cents,
    fees_cents,
    refund_amount_cents,
    number_of_sales,
  } = summary;
  console.log('summary', summary, 'period', period);
  // ───────────────────────── header ───────────────────────────
  const header = [
    `${business.name}`,
    `${business.address.street1}`,
    `${business.address.city}, ${business.address.state} ${business.address.postalCode}`,
    '', // blank line
    'date' in summary
      ? `Date: ${new Date((summary as DailySummaryType).date).toLocaleDateString('en-US', {
          timeZone: business?.configurations?.timeZone ?? 'America/Chicago',
        })}`
      : `Period: ${period ?? 'N/A'}`,
    '--------------------------------------',
  ].join('\n');

  // ───────────────────────── body ─────────────────────────────
  const lines = [
    makeLine('Gross Sales', cents(gross_sales_cents)),
    makeLine('Cash  ', cents(cash_sales_cents)),
    makeLine('Card  ', cents(card_sales_cents)),
    makeLine('Debt  ', cents(debt_amount_cents)),
    makeLine('Refunds', cents(refund_amount_cents)),
    makeLine('Taxes', cents(taxes_cents)),
    makeLine('Processing Fees', cents(fees_cents)),
    '--------------------------------------',
    makeLine('Transactions', number_of_sales.toString()),
  ];

  // payoutData only exists on DailySummary
  if ('payoutData' in summary) {
    const { beforeCutoff, afterCutoff } = summary.payoutData ?? {};
    lines.push(
      makeLine('Payout - Before', cents(beforeCutoff ?? 0)),
      makeLine('Payout - After ', cents(afterCutoff ?? 0))
    );
  }

  // ───────────────────────── footer ───────────────────────────
  const footer = '\nThank you for using PointCraft!\nAll amounts in USD.\n';

  return appendPointCraftFooter(`${header}\n${lines.join('\n')}${footer}`);
}

export function appendPointCraftFooter(receipt: string): string {
  const footerLines = [
    '', // blank line after the main body
    '-----------------------------------',
    'Powered by PointCraft®',
    'Smart POS & Payments for Modern Retail',
    'Support: +1 (612)473-5556',
    'Visit: point-craft.com/support',
  ];

  return `${receipt}${footerLines.join('\n')}`;
} // qr
const PORT = 'BT:TSP100'; // or 'USB:TSP100'
const EMU = Emulation.StarGraphic; // **keep StarGraphic**

/* ─── barcode string → data:image/png;base64,… ─── */
function barcodeDataUri(data: string): string {
  const canvas = document.createElement('canvas');

  JsBarcode(canvas, data, {
    format: 'CODE128',
    lineColor: '#000000',
    displayValue: true,
    width: 2,
    height: 50,
    margin: 10,
    fontSize: 12,
  });

  return canvas.toDataURL('image/png'); // FULL data-URI
}

/* ─── main print helper ─────────────────────────── */
export async function printReceiptWithBarcode(
  receiptText: string,
  barcodeData: string,
  opts: { port?: string; paperWidth?: 576 | 384 | 832 } = {}
) {
  const WIDTH = opts.paperWidth ?? 576;
  try {
    await withLock(async () => {
      await StarPRNT.printRasterReceipt(PORT, EMU, {
        text: receiptText,
        fontSize: 25,
        paperWidth: WIDTH,
        cutReceipt: false,
        openCashDrawer: false,
      });

      const dataUri = barcodeDataUri(barcodeData);

      await StarPRNT.printImage(PORT, EMU, {
        uri: dataUri,
        paperWidth: WIDTH,
        openCashDrawer: false,
      });

      await StarPRNT.print(PORT, EMU, [{ appendCutPaper: StarPRNT.CutPaperAction.PartialCutWithFeed }]);
    });
  } catch (error) {
    console.error('[PrinterStore] printReceiptWithBarcode failed:', error);
    usePrinterStore.setState({ lastError: captureError(error, 'barcode-receipt') });
    throw error;
  }
}

/* Draw text and barcode on one canvas, return data-URI */
/* replace only the body of receiptCanvas() – everything else unchanged */
/** ********************************************************************
 * receiptCanvas  —  one canvas, receipt → barcode → promo line
 ******************************************************************** */
function receiptCanvas(text: string, barcodeData: string, width = 576): string {
  /* ---------- layout values ---------- */
  const fontPx = 28; // semi-bold 28 px
  const font = `500 ${fontPx}px monospace`;
  const lineH = fontPx + 6;
  const gapAboveBC = 30; // gap above barcode
  const bcTargetW = width * 0.8; // 80 % width barcode
  const bcTargetH = 90;
  const promoGap = 10; // gap below barcode
  const promoText =
    'If you want to learn more about PointCraft follow us at point-craft.com/follow';
  const pad = 20;

  const bodyLines = text.split('\n');

  /* ---------- canvas size ------------ */
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height =
    pad +
    bodyLines.length * lineH +
    gapAboveBC +
    bcTargetH +
    promoGap +
    lineH + // only one promo line
    pad;
  const ctx = canvas.getContext('2d')!;

  /* ---------- background + font ------ */
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#000000';
  ctx.font = font;
  ctx.textBaseline = 'top';
  ctx.imageSmoothingEnabled = false;

  /* helper to right-align dollar amounts */
  const drawLine = (ln: string, y: number) => {
    const m = ln.match(/^(.*?)(\s+)(\$[-\d,.]+)\s*$/); // $ *required*
    if (m) {
      const [, label, , amt] = m;
      ctx.fillText(label.trimEnd(), 0, y);
      const amtW = ctx.measureText(amt).width;
      ctx.fillText(amt, width - amtW, y);
    } else {
      ctx.fillText(ln.trimEnd(), 0, y);
    }
  };

  /* ---------- body lines ------------- */
  let y = pad;
  bodyLines.forEach((ln) => {
    drawLine(ln, y);
    y += lineH;
  });

  /* ---------- barcode ---------------- */
  y += gapAboveBC;

  const bcCanvas = document.createElement('canvas');
  JsBarcode(bcCanvas, barcodeData, {
    format: 'CODE128',
    width: 2,
    height: bcTargetH,
    displayValue: true,
    fontSize: 20,
    margin: 0,
    textMargin: 6,
  });

  const scale = bcTargetW / bcCanvas.width;
  const bcW = bcCanvas.width * scale;
  const bcH = bcCanvas.height * scale;
  const bcX = (width - bcW) / 2;

  ctx.drawImage(bcCanvas, bcX, y, bcW, bcH);

  /* ---------- promo line ------------- */
  y += bcH + promoGap;
  drawLine(promoText, y);

  return canvas.toDataURL('image/png');
}

/* Main printer helper */
export async function printReceiptOneBitmap(
  receiptText: string,
  barcodeData: string,
  opts: { port?: string; paperWidth?: 576 | 384 | 832 } = {}
) {
  const WIDTH = opts.paperWidth ?? 576;
  const dataUri = receiptCanvas(receiptText, barcodeData, WIDTH);
  try {
    await withLock(async () => {
      await StarPRNT.printImage(PORT, EMU, {
        uri: dataUri,
        paperWidth: WIDTH,
        cutReceipt: true,
      });
    });
  } catch (error) {
    console.error('[PrinterStore] printReceiptOneBitmap failed:', error);
    usePrinterStore.setState({ lastError: captureError(error, 'bitmap-receipt') });
    throw error;
  }
}

export { printOrderReceiptOneBitmap } from './orderPrinter';
// Modern V4 kitchen order ticket (Normal/Large bitmap). Supersedes the legacy
// printOrderReceiptOneBitmap at all call sites; size comes from the saved preference.
export { printOrderReceiptBitmap } from './v4/order-receipt';
